// Factures — liste des factures avec export CSV
import { useState, useCallback, useMemo } from "react";
import { FileText, AlertCircle, Download, Search } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useFinanceInvoices } from "@/hooks/admin/useFinance";
import type { FinanceInvoice, FinanceInvoiceStatus } from "@/lib/admin-api/finance";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<FinanceInvoiceStatus, string> = {
  draft: "bg-stone-100 text-stone-700 border-stone-200",
  sent: "bg-blue-100 text-blue-800 border-blue-200",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  overdue: "bg-red-100 text-red-800 border-red-200",
  cancelled: "bg-stone-100 text-stone-500 border-stone-200",
};

const STATUS_LABELS: Record<FinanceInvoiceStatus, string> = {
  draft: "Brouillon",
  sent: "Envoyée",
  paid: "Payée",
  overdue: "En retard",
  cancelled: "Annulée",
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number): string {
  return `${amount.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

function exportToCSV(invoices: readonly FinanceInvoice[]): void {
  const headers = [
    "Numéro",
    "Client",
    "HT (FCFA)",
    "TVA (FCFA)",
    "TTC (FCFA)",
    "Statut",
    "Émis le",
    "Dû le",
    "Payé le",
  ];
  const rows = invoices.map((inv) => [
    inv.invoice_number,
    inv.client_name,
    inv.amount_ht_fcfa,
    inv.tva_fcfa,
    inv.amount_ttc_fcfa,
    STATUS_LABELS[inv.status],
    formatDate(inv.issued_at),
    formatDate(inv.due_at),
    inv.paid_at ? formatDate(inv.paid_at) : "",
  ]);
  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `factures_${new Date().toISOString().split("T")[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Invoices() {
  const [statusFilter, setStatusFilter] = useState<FinanceInvoiceStatus | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => ({
      status: statusFilter || null,
      date_from: dateFrom || null,
      date_to: dateTo || null,
      q: search || null,
      page,
      limit: 50,
    }),
    [statusFilter, dateFrom, dateTo, search, page],
  );

  const { data, isLoading, isError, refetch } = useFinanceInvoices(params);
  const invoices = data?.invoices ?? [];

  const thisMonthTotal = useMemo(
    () => invoices.reduce((sum, inv) => sum + inv.amount_ttc_fcfa, 0),
    [invoices],
  );
  const paidCount = useMemo(
    () => invoices.filter((inv) => inv.status === "paid").length,
    [invoices],
  );
  const overdueCount = useMemo(
    () => invoices.filter((inv) => inv.status === "overdue").length,
    [invoices],
  );
  const cancelledCount = useMemo(
    () => invoices.filter((inv) => inv.status === "cancelled").length,
    [invoices],
  );

  const handleExport = useCallback(() => {
    exportToCSV(invoices);
  }, [invoices]);

  const columns: readonly ColumnDef<FinanceInvoice>[] = useMemo(
    () => [
      {
        id: "invoice_number",
        header: "Numéro",
        cell: (inv) => (
          <span className="font-mono text-xs font-medium text-stone-800">
            {inv.invoice_number}
          </span>
        ),
      },
      {
        id: "client_name",
        header: "Client",
        cell: (inv) => (
          <span className="text-sm font-medium text-stone-900">
            {inv.client_name}
          </span>
        ),
      },
      {
        id: "amount_ht_fcfa",
        header: "HT",
        cell: (inv) => (
          <span className="text-sm text-stone-600">
            {formatFcfa(inv.amount_ht_fcfa)}
          </span>
        ),
      },
      {
        id: "tva_fcfa",
        header: "TVA",
        cell: (inv) => (
          <span className="text-sm text-stone-500">
            {formatFcfa(inv.tva_fcfa)}
          </span>
        ),
      },
      {
        id: "amount_ttc_fcfa",
        header: "TTC",
        cell: (inv) => (
          <span className="text-sm font-semibold text-stone-900">
            {formatFcfa(inv.amount_ttc_fcfa)}
          </span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (inv) => (
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[inv.status]}`}
          >
            {STATUS_LABELS[inv.status]}
          </span>
        ),
      },
      {
        id: "issued_at",
        header: "Émis le",
        cell: (inv) => (
          <span className="text-sm text-stone-500">
            {formatDate(inv.issued_at)}
          </span>
        ),
      },
      {
        id: "due_at",
        header: "Dû le",
        cell: (inv) => (
          <span className="text-sm text-stone-500">
            {formatDate(inv.due_at)}
          </span>
        ),
      },
      {
        id: "paid_at",
        header: "Payé le",
        cell: (inv) => (
          <span className="text-sm text-stone-500">
            {inv.paid_at ? formatDate(inv.paid_at) : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Factures
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement…"
                  : `${data?.pagination.total ?? 0} facture(s)`}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={invoices.length === 0}
            className="gap-1.5"
          >
            <Download className="h-4 w-4" />
            Exporter CSV
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="Total facturé ce mois"
            value={thisMonthTotal}
            format="currency"
            icon={<FileText className="h-4 w-4" />}
          />
          <KPICard
            label="Payées"
            value={paidCount}
            format="number"
            icon={<FileText className="h-4 w-4" />}
          />
          <KPICard
            label="En retard"
            value={overdueCount}
            format="number"
            icon={<AlertCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Annulées"
            value={cancelledCount}
            format="number"
            icon={<FileText className="h-4 w-4" />}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Rechercher client…"
              className="h-9 w-full rounded-md border border-stone-200 pl-9 pr-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as FinanceInvoiceStatus | "");
              setPage(1);
            }}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Tous les statuts</option>
            <option value="draft">Brouillon</option>
            <option value="sent">Envoyée</option>
            <option value="paid">Payée</option>
            <option value="overdue">En retard</option>
            <option value="cancelled">Annulée</option>
          </select>
          <div className="flex items-center gap-1.5">
            <label className="text-sm font-medium text-stone-600">Du :</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-sm font-medium text-stone-600">Au :</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          {(statusFilter || dateFrom || dateTo || search) && (
            <button
              type="button"
              onClick={() => {
                setStatusFilter("");
                setDateFrom("");
                setDateTo("");
                setSearch("");
                setPage(1);
              }}
              className="text-sm text-stone-500 hover:text-stone-700"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les factures.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && invoices.length === 0 ? (
              <EmptyState
                icon={<FileText className="h-6 w-6" />}
                title="Aucune facture"
                description="Les factures émises apparaîtront ici."
              />
            ) : (
              <AdminDataTable
                data={invoices}
                columns={columns}
                isLoading={isLoading}
                pagination={
                  data?.pagination
                    ? {
                        page: data.pagination.page,
                        limit: data.pagination.limit,
                        total: data.pagination.total,
                      }
                    : undefined
                }
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
